import { TEXT_PRIMARY, TEXT_BODY, ACCENT_GOLD, GOLD } from '../lib/medievalTheme';

// Unified link pattern; URL prefix selects card-vs-web behavior so we
// don't double-scan the same input.
const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)/;
const ELEMENT_WORD_PATTERN = /\b(fire|water|earth|air)\b/i;

function openExternalUrl(url) {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function normalizeElement(word) {
  const lower = word.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Same bare-URL extractor as MarkdownEditor. Stops at whitespace,
// brackets, parens, quotes, then trims trailing sentence
// punctuation so "see https://x.com." doesn't include the period.
function detectBareUrl(text) {
  const match = text.match(/https?:\/\/[^\s\[\]()<>"']+/);
  if (!match) return null;
  let url = match[0];
  while (/[.,;:!?]$/.test(url) && url.length > 9) url = url.slice(0, -1);
  return { url, index: match.index };
}

/**
 * Lightweight markdown renderer used in two places:
 *   1. GitHub release notes pulled into the in-game news mail
 *   2. Per-deck strategy notes in the deck editor side panel
 *
 * Supported subset:
 *   # / ## / ### Headings (sized down by level)
 *   - bullets (with nested **bold**, *italic*, `code` inline)
 *   * bullets (alias of -)
 *   1. ordered list items
 *   [Name](card:unique_id). Inline card pill (click → inspector,
 *     hover → preview), opt-in via the onCardClick / onCardHover
 *     callbacks so the release-notes use-site can stay handler-free
 *   **bold**, *italic*, `code` inline spans
 *   Blank-line paragraph breaks
 *
 * No external dependencies. Just string splitting and inline regex.
 */
export default function MarkdownNotes({ text, onCardClick, onCardHover, onCardHoverEnd }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  const inlineCtx = { onCardClick, onCardHover, onCardHoverEnd };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;

    const heading = matchHeading(trimmed);
    if (heading) {
      elements.push(renderHeading(key++, heading.level, heading.text, elements.length === 0, inlineCtx));
      continue;
    }

    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (bulletMatch) {
      elements.push(
        <div
          key={key++}
          className="flex gap-2 text-xs leading-relaxed"
          style={{ color: TEXT_BODY, marginBottom: '0.3cqw', paddingLeft: '0.25cqw' }}
        >
          <span
            style={{
              color: ACCENT_GOLD,
              flexShrink: 0,
              fontSize: '0.875cqw',
              lineHeight: 1,
              marginTop: '0.15cqw',
            }}
          >
            &#x2022;
          </span>
          <span>{renderInline(bulletMatch[1], inlineCtx)}</span>
        </div>
      );
      continue;
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      elements.push(
        <div
          key={key++}
          className="flex gap-2 text-xs leading-relaxed"
          style={{ color: TEXT_BODY, marginBottom: '0.3cqw', paddingLeft: '0.25cqw' }}
        >
          <span
            style={{
              color: ACCENT_GOLD,
              flexShrink: 0,
              fontWeight: 600,
              minWidth: '1.25cqw',
            }}
          >
            {orderedMatch[1]}.
          </span>
          <span>{renderInline(orderedMatch[2], inlineCtx)}</span>
        </div>
      );
      continue;
    }

    elements.push(
      <p
        key={key++}
        className="text-xs leading-relaxed"
        style={{ color: TEXT_BODY, marginBottom: '0.375cqw' }}
      >
        {renderInline(trimmed, inlineCtx)}
      </p>
    );
  }

  return <div>{elements}</div>;
}

function matchHeading(line) {
  const match = line.match(/^(#{1,3})\s+(.+)$/);
  if (!match) return null;
  return { level: match[1].length, text: match[2] };
}

function renderHeading(key, level, text, isFirst, inlineCtx) {
  const marginTop = isFirst ? '0' : '0.875cqw';
  if (level === 1) {
    return (
      <h2
        key={key}
        className="font-bold arena-heading"
        style={{
          color: TEXT_PRIMARY,
          fontSize: '1.25cqw',
          lineHeight: 1.2,
          marginTop,
          marginBottom: '0.5cqw',
          paddingBottom: '0.3cqw',
          borderBottom: `1px solid ${GOLD} 0.25)`,
          letterSpacing: '0.02em',
        }}
      >
        {renderInline(text, inlineCtx)}
      </h2>
    );
  }
  if (level === 2) {
    return (
      <h3
        key={key}
        className="font-bold arena-heading"
        style={{
          color: ACCENT_GOLD,
          fontSize: '0.875cqw',
          marginTop,
          marginBottom: '0.375cqw',
          paddingBottom: '0.25cqw',
          borderBottom: `1px solid ${GOLD} 0.12)`,
        }}
      >
        {renderInline(text, inlineCtx)}
      </h3>
    );
  }
  return (
    <h4
      key={key}
      className="font-semibold arena-heading"
      style={{
        color: ACCENT_GOLD,
        fontSize: '0.75cqw',
        marginTop,
        marginBottom: '0.25cqw',
      }}
    >
      {renderInline(text, inlineCtx)}
    </h4>
  );
}

// Exported so other surfaces (e.g. comment bodies in the deck-notes
// panel) can reuse the same inline pattern set without dragging the
// block-level renderer in.
export function renderInline(text, inlineCtx = {}) {
  const { onCardClick, onCardHover, onCardHoverEnd, onPlayerClick } = inlineCtx;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const linkMatch = remaining.match(LINK_PATTERN);
    const elementMatch = remaining.match(ELEMENT_WORD_PATTERN);
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`(.+?)`/);
    const italicMatch = remaining.match(/(?:^|[^*])\*([^*\s][^*]*?)\*(?!\*)/);
    const bareUrl = detectBareUrl(remaining);

    let linkCandidate = null;
    if (linkMatch) {
      const url = linkMatch[2];
      if (url.startsWith('card:')) {
        linkCandidate = { type: 'card', index: linkMatch.index, full: linkMatch[0], name: linkMatch[1], cardId: url.slice('card:'.length) };
      } else if (url.startsWith('player:')) {
        linkCandidate = { type: 'player', index: linkMatch.index, full: linkMatch[0], name: linkMatch[1], playerId: url.slice('player:'.length) };
      } else if (/^https?:\/\//i.test(url)) {
        linkCandidate = { type: 'web', index: linkMatch.index, full: linkMatch[0], text: linkMatch[1], url };
      }
    }

    const candidates = [
      linkCandidate,
      elementMatch && {
        type: 'element',
        index: elementMatch.index,
        full: elementMatch[0],
        word: elementMatch[1],
        element: normalizeElement(elementMatch[1]),
      },
      boldMatch && { type: 'bold', index: boldMatch.index, full: boldMatch[0], inner: boldMatch[1] },
      codeMatch && { type: 'code', index: codeMatch.index, full: codeMatch[0], inner: codeMatch[1] },
      italicMatch && {
        type: 'italic',
        index: italicMatch.index + (italicMatch[0].startsWith('*') ? 0 : 1),
        full: italicMatch[0].startsWith('*') ? italicMatch[0] : italicMatch[0].slice(1),
        inner: italicMatch[1],
      },
      bareUrl && { type: 'web', index: bareUrl.index, full: bareUrl.url, text: bareUrl.url, url: bareUrl.url },
    ].filter(Boolean).sort((a, b) => a.index - b.index);

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const m = candidates[0];
    if (m.index > 0) parts.push(remaining.slice(0, m.index));

    if (m.type === 'player') {
      // #mention (player). Same pill as an @card link; click opens the profile.
      const playerId = m.playerId;
      parts.push(
        <button
          key={key++}
          type="button"
          className="md-card-pill"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onPlayerClick) onPlayerClick(playerId);
          }}
        >
          {m.name}
        </button>
      );
    } else if (m.type === 'card') {
      // Pill click hands off the card unique_id to the host surface
      // The deck side panel knows how to render a card inspector.
      const id = m.cardId;
      parts.push(
        <button
          key={key++}
          type="button"
          className="md-card-pill"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onCardClick) onCardClick(id);
          }}
          onMouseEnter={(e) => { if (onCardHover) onCardHover(id, e.currentTarget); }}
          onMouseLeave={() => { if (onCardHoverEnd) onCardHoverEnd(); }}
        >
          {m.name}
        </button>
      );
    } else if (m.type === 'web') {
      const url = m.url;
      parts.push(
        <button
          key={key++}
          type="button"
          className="md-web-pill"
          title={url}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openExternalUrl(url);
          }}
        >
          {m.text}
        </button>
      );
    } else if (m.type === 'element') {
      parts.push(
        <span
          key={key++}
          className={`md-element md-element-${m.element.toLowerCase()}`}
          data-element={m.element}
        >
          <img
            className="md-element-icon"
            src={`/game-assets/gameboard-tokens/${m.element.toLowerCase()}-token.svg`}
            alt=""
            draggable={false}
          />
          {m.word}
        </span>
      );
    } else if (m.type === 'bold') {
      parts.push(
        <strong key={key++} style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          {m.inner}
        </strong>
      );
    } else if (m.type === 'italic') {
      parts.push(
        <em key={key++} style={{ fontStyle: 'italic', color: TEXT_PRIMARY }}>
          {m.inner}
        </em>
      );
    } else if (m.type === 'code') {
      parts.push(
        <code
          key={key++}
          style={{
            color: ACCENT_GOLD,
            background: `${GOLD} 0.08)`,
            padding: '0.0625cqw 0.25cqw',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.625cqw',
          }}
        >
          {m.inner}
        </code>
      );
    }

    remaining = remaining.slice(m.index + m.full.length);
  }

  return parts;
}
